import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo } from "react";

import { Filter, FilterId, FilterType } from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { CombinedNode } from "@/pages/scenario-builder/utils/nodes";
import { getMappedName } from "@/Utils/mapping-utils";

import { useAllPublishers } from "./api/getAllPublishers";
import { usePublishersStore } from "./store/usePublishersStore";

export const useAvailableFilters = ({
  selectedNode,
  startDate,
  endDate,
}: {
  selectedNode?: CombinedNode;
  startDate?: string;
  endDate?: string;
}) => {
  const { setTargetNode, setChannels, setPublishers, setGeographies } = usePublishersStore();
  const { targetNode, channels, geographies, publishers } = usePublishersStore(
    (state) => state.filters
  );
  const { initInfo: info } = useInfo();
  const [geoData] = info?.geographies || [];

  const { data: allPublishers } = useAllPublishers({
    startDate,
    endDate,
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
  });

  const handleTargetNodeChange = useCallback(
    (targetNode: string | null) => {
      setTargetNode(targetNode);
    },
    [setTargetNode]
  );

  const handleChannelsChange = useCallback(
    (channel: string[]) => setChannels(channel),
    [setChannels]
  );

  const handlePublishersChange = useCallback(
    (publisher: string[]) => setPublishers(publisher),
    [setPublishers]
  );

  const handleGeographiesChange = useCallback(
    (geography: string[]) => setGeographies(geography),
    [setGeographies]
  );

  const publisherOptions = useMemo(() => {
    if (!allPublishers) return [];

    return allPublishers
      .map((publisher) => ({
        label: getMappedName(publisher) || publisher,
        value: publisher,
        selected: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allPublishers]);

  const targetNodeFilter = useMemo(
    () => ({
      id: FilterId.BusinessUnit,
      label: "Business Unit",
      type: FilterType.BusinessUnit,
      value: targetNode,
      onChange: handleTargetNodeChange,
    }),
    [targetNode, handleTargetNodeChange]
  );

  const channelsFilter = useMemo(
    () => ({
      id: FilterId.Channel,
      label: "Channels",
      type: FilterType.MultiSelect,
      value: channels,
      onChange: handleChannelsChange,
    }),
    [channels, handleChannelsChange]
  );

  const publishersFilter = useMemo(
    () => ({
      id: FilterId.Publisher,
      label: "Publishers",
      type: FilterType.MultiSelect,
      value: publishers ?? [],
      onChange: handlePublishersChange,
      options: publisherOptions,
    }),
    [publishers, handlePublishersChange, publisherOptions]
  );

  const geographiesFilter = useMemo(
    () => ({
      id: FilterId.Geography,
      label: "Geo",
      type: FilterType.MultiSelect,
      value: geographies,
      onChange: handleGeographiesChange,
    }),
    [geographies, handleGeographiesChange]
  );

  useEffect(() => {
    if (!isEmpty(allPublishers)) {
      setPublishers(allPublishers || []);
    }
  }, [allPublishers, setPublishers]);

  return useMemo(() => {
    const filters: Filter[] = [targetNodeFilter, channelsFilter, publishersFilter];
    if (geoData && geoData.length > 0) {
      filters.push(geographiesFilter);
    }

    return filters;
  }, [targetNodeFilter, channelsFilter, geographiesFilter, geoData, publishersFilter]);
};
